/**
 * Was der Prüfschritt über einzelne Tage sagen darf: nur, was das geladene Fenster aus
 * `rateSource`/`minimumStaySource` belegt. Die Zahlen für den ganzen Zeitraum kommen vom Server
 * (`ari/ownership/impact`); Tage außerhalb des Fensters nennt der Prüfschritt nicht einzeln.
 */
import type { AriPushBatchRange, AvailabilityDistributionDay } from '@/shared/api';
import type { InlineEditorValues } from '../calendar/inlineEditorModel';
import type { SelectedRange } from '../calendar/rangeSelectionModel';

export interface ReviewDays {
  /** Tage, deren Preis von der Automatik in die Hand wechselt. */
  rate: string[];
  /** Tage, deren Mindestnächte von der Automatik in die Hand wechseln. */
  minStay: string[];
  /** Tage, an denen Preis oder Mindestnächte schon von Hand gepflegt sind (wie der Server zählt). */
  alreadyManual: string[];
}

export function reviewDays(
  days: readonly AvailabilityDistributionDay[],
  ranges: readonly SelectedRange[],
  sets: { rate: boolean; minStay: boolean },
): ReviewDays {
  const inRanges = days.filter((day) =>
    ranges.some((range) => range.from <= day.date && range.to >= day.date),
  );
  const pick = (test: (day: AvailabilityDistributionDay) => boolean) =>
    inRanges.filter(test).map((day) => day.date);
  return {
    rate: sets.rate ? pick((day) => day.rateSource === 'Automation') : [],
    minStay: sets.minStay ? pick((day) => day.minimumStaySource === 'Automation') : [],
    alreadyManual:
      sets.rate || sets.minStay
        ? pick((day) => day.rateSource === 'Manual' || day.minimumStaySource === 'Manual')
        : [],
  };
}

/** Felder, deren Eingabe der Server nicht annähme – sie gingen sonst still als „unverändert“ raus. */
export function invalidFields(
  values: InlineEditorValues,
  range: AriPushBatchRange | undefined,
): string[] {
  if (!range) return [];
  const errors: string[] = [];
  if (
    values.rate.trim() &&
    !(range.rate != null && Number.isFinite(range.rate) && range.rate >= 0)
  ) {
    errors.push('Preis: eine Zahl ab 0');
  }
  if (values.minStay.trim() && range.minStay == null) errors.push('Mindestnächte: ganze Zahl ab 1');
  if (values.maxStay.trim() && range.maxStay == null) {
    errors.push('Höchstnächte: ganze Zahl ab 0 (0 = unbegrenzt)');
  }
  return errors;
}

const RESTRICTION_WORDS = {
  closedToArrival: 'Keine Anreise',
  closedToDeparture: 'Keine Abreise',
  stopSell: 'Verkaufsstopp',
} as const;

/** Was gesetzt wird, kurz: „Preis 140 € · Mindestnächte 3 · Keine Anreise aufgehoben“. */
export function valuesSummary(values: InlineEditorValues, currency = 'EUR'): string {
  const parts: string[] = [];
  if (values.availability === 'blocked') parts.push('blockieren');
  if (values.availability === 'available') parts.push('freigeben');
  if (values.rate.trim())
    parts.push(`Preis ${values.rate.trim()} ${currency === 'EUR' ? '€' : currency}`);
  if (values.minStay.trim()) parts.push(`Mindestnächte ${values.minStay.trim()}`);
  if (values.maxStay.trim()) {
    parts.push(
      values.maxStay.trim() === '0'
        ? 'Höchstnächte unbegrenzt'
        : `Höchstnächte ${values.maxStay.trim()}`,
    );
  }
  for (const key of Object.keys(RESTRICTION_WORDS) as (keyof typeof RESTRICTION_WORDS)[]) {
    if (values[key] === 'set') parts.push(`${RESTRICTION_WORDS[key]} setzen`);
    if (values[key] === 'clear') parts.push(`${RESTRICTION_WORDS[key]} aufheben`);
  }
  return parts.join(' · ');
}
